import { ref } from '@vue/composition-api'

/**
 * 予約重複チェック用のコンポーザブル
 *
 * バックエンドが 409 Conflict + { status: 'reservation_conflict' } を返した場合に
 * 確認ダイアログを表示し、ユーザーの選択に応じてリトライを制御する。
 *
 * @example
 * const { handleConflictResponse, ... } = useReservationConflictCheck()
 *
 * try {
 *   const res = await TableApi.createTable(params)
 * } catch (e) {
 *   if (handleConflictResponse(e)) {
 *     // 409 → ダイアログ表示中。confirmProceed/cancelProceed で解決される
 *     const confirmed = await waitForConfirmation()
 *     if (confirmed) {
 *       // confirm_reservation_conflict: true を付けてリトライ
 *     }
 *   }
 * }
 *
 * // または簡易ラッパーを使用:
 * const res = await executeWithConflictCheck(
 *   () => TableApi.createTable(params),                      // 初回実行
 *   () => TableApi.createTable({ ...params, confirmReservationConflict: true }), // リトライ
 * )
 */
export default () => {
  const conflicts = ref([])
  const showConflictDialog = ref(false)

  let pendingResolve = null

  /**
   * axios エラーが予約重複(409)かどうか判定し、ダイアログを表示する
   * @param {Error} error - axiosエラー
   * @returns {boolean} 予約重複だった場合 true
   */
  const handleConflictResponse = error => {
    const data = error?.response?.data
    if (error?.response?.status !== 409 || data?.status !== 'reservation_conflict') {
      return false
    }

    conflicts.value = data.conflicts || []
    showConflictDialog.value = true

    return true
  }

  /**
   * ダイアログの結果をPromiseで待つ
   * @returns {Promise<boolean>} ユーザーが「作成する」を選んだら true
   */
  const waitForConfirmation = () => {
    return new Promise(resolve => {
      pendingResolve = resolve
    })
  }

  const confirmProceed = () => {
    showConflictDialog.value = false
    if (pendingResolve) {
      pendingResolve(true)
      pendingResolve = null
    }
  }

  const cancelProceed = () => {
    showConflictDialog.value = false
    if (pendingResolve) {
      pendingResolve(false)
      pendingResolve = null
    }
  }

  /**
   * API呼び出しを予約重複チェック付きで実行する簡易ラッパー
   *
   * @param {Function} execute - 初回のAPI呼び出し
   * @param {Function} retryWithConfirm - confirm付きのリトライAPI呼び出し
   * @returns {Promise<any|null>} APIレスポンス。キャンセルされた場合 null
   */
  const executeWithConflictCheck = async (execute, retryWithConfirm) => {
    try {
      return await execute()
    } catch (error) {
      if (!handleConflictResponse(error)) {
        throw error // 予約重複以外のエラーはそのまま投げる
      }

      const confirmed = await waitForConfirmation()
      if (!confirmed) return null

      return retryWithConfirm()
    }
  }

  /**
   * 予約の時間とステータスを表示用にフォーマット
   */
  const formatConflict = conflict => {
    const statusLabels = {
      confirmed: '確定',
      checked_in: '来店中',
      completed: '完了',
    }
    const timeMatch = (conflict.start_time || '').match(/(\d{2}:\d{2})/)
    const endMatch = (conflict.end_time || '').match(/(\d{2}:\d{2})/)
    const time = timeMatch ? timeMatch[1] : ''
    const end = endMatch ? ` 〜 ${endMatch[1]}` : ''
    const status = statusLabels[conflict.status] || conflict.status

    return `${time}${end} (${status})`
  }

  return {
    conflicts,
    showConflictDialog,
    handleConflictResponse,
    waitForConfirmation,
    confirmProceed,
    cancelProceed,
    executeWithConflictCheck,
    formatConflict,
  }
}
